/* ============================================================
   SINGSPACE ADMIN - MODALE MARQUAGE PAIEMENT MANUEL
   Permet de spécifier le mode (Chèque/Virement), la date et le justificatif
   ============================================================ */

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/services/api';
import { Building2, CheckCircle2, FileText, Loader2, Ticket, Upload, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface MarkAsPaidModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: { method: string; date: string; proofDocumentUrl: string }) => Promise<void>;
  bookingReference?: string;
  totalAmount?: number;
}

export function MarkAsPaidModal({ open, onClose, onConfirm, bookingReference, totalAmount }: MarkAsPaidModalProps) {
  const [method, setMethod] = useState<'CHEQUE' | 'VIREMENT'>('VIREMENT');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [proofDocument, setProofDocument] = useState<File | null>(null);
  const [proofDocumentUrl, setProofDocumentUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string>('');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Réinitialiser l'erreur
    setError('');

    // Validation du type de fichier
    if (file.type !== 'application/pdf') {
      setError('Seuls les fichiers PDF sont acceptés.');
      setProofDocument(null);
      setProofDocumentUrl('');
      return;
    }

    // Validation de la taille
    if (file.size > 12 * 1024 * 1024) {
      setError('Le fichier est trop volumineux. Taille maximale : 12 MB.');
      setProofDocument(null);
      setProofDocumentUrl('');
      return;
    }

    setProofDocument(file);

    // Upload immédiat du fichier
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        setProofDocumentUrl(response.data.data.url);
        toast.success('Justificatif téléversé avec succès');
      } else {
        setError(response.data.error || 'Erreur lors du téléversement du justificatif');
        setProofDocument(null);
        setProofDocumentUrl('');
      }
    } catch (err: any) {
      console.error('[MarkAsPaidModal] Upload error:', err);

      // Gestion des erreurs spécifiques
      let errorMessage = 'Erreur lors du téléversement';

      if (err.response?.status === 400) {
        errorMessage = err.response?.data?.error || 'Fichier invalide';
      } else if (err.response?.status === 413 || err.code === 'ERR_BAD_REQUEST') {
        errorMessage = 'Le fichier est trop volumineux (max 12 MB)';
      } else if (err.response?.status === 500) {
        errorMessage = 'Erreur serveur. Veuillez réessayer.';
      } else if (!navigator.onLine) {
        errorMessage = 'Pas de connexion internet';
      }

      setError(errorMessage);
      setProofDocument(null);
      setProofDocumentUrl('');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    if (!proofDocumentUrl) {
      setError('Veuillez téléverser un justificatif de paiement.');
      return;
    }

    setLoading(true);
    try {
      await onConfirm({ method, date, proofDocumentUrl });
      onClose();
    } catch (error) {
      console.error('[MarkAsPaidModal] Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => !loading && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden border-none shadow-2xl">
        <DialogHeader className="p-6 bg-slate-50 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-100 text-emerald-700 rounded-xl">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div className="text-left">
              <DialogTitle className="text-xl font-bold text-slate-900 leading-tight">
                Confirmer le paiement
              </DialogTitle>
              <DialogDescription className="text-slate-500 font-medium mt-1">
                Enregistrement manuel du règlement
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="p-6 space-y-6 bg-white">
          {/* Récapitulatif Rapide */}
          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
            <div className="text-left">
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-0.5">Référence</p>
              <p className="text-sm font-bold text-slate-700">{bookingReference || 'N/A'}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-0.5">Montant Total</p>
              <p className="text-lg font-black text-emerald-600">{totalAmount?.toLocaleString('fr-FR')} XAF</p>
            </div>
          </div>

          {/* Message d'avertissement */}
          <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-2xl">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold text-sm mt-0.5">
                !
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-bold text-amber-900 mb-1">Attention : Action irréversible</p>
                <p className="text-xs text-amber-800 leading-relaxed">
                  Une fois le devis marqué comme payé, vous ne pourrez plus modifier la tarification. Assurez-vous que
                  tous les montants sont corrects avant de valider.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {/* Mode de règlement */}
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 block text-left">
                Mode de règlement
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setMethod('VIREMENT')}
                  className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all group ${
                    method === 'VIREMENT'
                      ? 'border-emerald-500 bg-emerald-50/50 shadow-sm'
                      : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <Building2
                    className={`w-6 h-6 ${method === 'VIREMENT' ? 'text-emerald-600' : 'text-slate-400 group-hover:text-slate-600'}`}
                  />
                  <span
                    className={`text-sm font-bold ${method === 'VIREMENT' ? 'text-emerald-700' : 'text-slate-500 group-hover:text-slate-700'}`}
                  >
                    Virement
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setMethod('CHEQUE')}
                  className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all group ${
                    method === 'CHEQUE'
                      ? 'border-emerald-500 bg-emerald-50/50 shadow-sm'
                      : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <Ticket
                    className={`w-6 h-6 ${method === 'CHEQUE' ? 'text-emerald-600' : 'text-slate-400 group-hover:text-slate-600'}`}
                  />
                  <span
                    className={`text-sm font-bold ${method === 'CHEQUE' ? 'text-emerald-700' : 'text-slate-500 group-hover:text-slate-700'}`}
                  >
                    Chèque
                  </span>
                </button>
              </div>
            </div>

            {/* Date de remise */}
            <div>
              <label
                htmlFor="payment-date"
                className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block text-left"
              >
                Date de règlement
              </label>
              <div className="relative group">
                <input
                  id="payment-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-4 py-3.5 bg-slate-50 border-2 border-slate-100 rounded-2xl text-slate-700 font-bold focus:outline-none focus:border-emerald-500 focus:bg-white transition-all caret-emerald-500"
                />
              </div>
            </div>

            {/* Upload du justificatif */}
            <div>
              <label
                htmlFor="proof-document"
                className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block text-left"
              >
                Justificatif de paiement
              </label>
              <div className="border-2 border-dashed border-slate-300 rounded-2xl p-6 text-center hover:border-emerald-500 transition-colors">
                <input
                  type="file"
                  id="proof-document"
                  accept=".pdf,application/pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={uploading}
                />
                <label htmlFor="proof-document" className="cursor-pointer">
                  <div className="flex flex-col items-center gap-2">
                    {uploading ? (
                      <>
                        <Loader2 className="w-10 h-10 text-emerald-600 animate-spin" />
                        <p className="text-sm font-medium text-slate-800">Téléversement en cours...</p>
                        <p className="text-xs text-slate-500">Veuillez patienter</p>
                      </>
                    ) : proofDocument ? (
                      <>
                        <div className="p-3 bg-emerald-100 rounded-full">
                          <FileText className="w-10 h-10 text-emerald-600" />
                        </div>
                        <p className="text-sm font-medium text-slate-800">{proofDocument.name}</p>
                        <p className="text-xs text-emerald-600 font-semibold">
                          {(proofDocument.size / 1024).toFixed(2)} KB • Téléversé ✓
                        </p>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setProofDocument(null);
                            setProofDocumentUrl('');
                            const input = document.getElementById('proof-document') as HTMLInputElement;
                            if (input) input.value = '';
                          }}
                          className="text-xs text-red-600 hover:text-red-700 font-medium mt-1"
                        >
                          Changer de fichier
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="p-3 bg-slate-100 rounded-full">
                          <Upload className="w-10 h-10 text-slate-400" />
                        </div>
                        <p className="text-sm font-medium text-slate-800">Cliquez pour téléverser</p>
                        <p className="text-xs text-slate-500">Format PDF uniquement (max 12 MB)</p>
                      </>
                    )}
                  </div>
                </label>
              </div>
              <p className="text-xs text-slate-500 mt-2 text-left">
                Téléversez le scan du chèque, le relevé bancaire ou tout autre justificatif de paiement au format PDF.
              </p>
            </div>
          </div>

          {/* Message d'erreur */}
          {error && (
            <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-start gap-3">
              <div className="flex-shrink-0 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center font-bold text-xs mt-0.5">
                !
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-red-900 mb-1">Erreur</p>
                <p className="text-sm text-red-800">{error}</p>
              </div>
              <button
                onClick={() => setError('')}
                className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <DialogFooter className="p-6 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row gap-3">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={loading || uploading}
            className="flex-1 rounded-xl h-12 text-slate-500 font-bold hover:bg-slate-200 transition-all sm:order-1"
          >
            <X className="w-4 h-4 mr-2" />
            Annuler
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || uploading || !proofDocumentUrl}
            className="flex-1 rounded-xl h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-lg shadow-emerald-600/20 transition-all transform active:scale-[0.98] sm:order-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2" />
                Traitement...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Valider
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
